import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../auth/AuthContext';
import { resendVerification } from '../../api/auth';
import { apiErrorMessage } from '../../api/client';
import { AuthInput } from '../../components/auth/AuthInput';

const RESEND_COOLDOWN_S = 30;

export default function VerifyEmail() {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { email?: string; sent?: boolean } | null;
  const stateEmail = navState?.email ?? '';
  const codeAlreadySent = navState?.sent === true;

  const [email, setEmail] = useState(stateEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await verifyEmail(email, code);
      toast.success('Email verified — welcome!');
      navigate('/');
    } catch (err) {
      setError(apiErrorMessage(err, 'Verification failed'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    try {
      await resendVerification(email);
      setCooldown(RESEND_COOLDOWN_S);
      setCode('');
      setError('');
      toast.success('New code sent — check your inbox');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not resend the code'));
    }
  }

  return (
    <section data-testid="page-verifyemail" className="mx-auto max-w-sm pt-6">
      <h1 className="text-2xl font-bold">Check your email</h1>
      <p className="mt-2 text-sm text-stone-600">
        {stateEmail && codeAlreadySent ? (
          <>
            We sent a 6-digit code to <span className="font-semibold">{stateEmail}</span>. Enter it
            below to activate your account.
          </>
        ) : stateEmail ? (
          <>
            <span className="font-semibold">{stateEmail}</span> isn't verified yet. Hit "Resend
            code", then enter the code from your email.
          </>
        ) : (
          'Enter the email you registered with and the 6-digit code we sent you.'
        )}
      </p>
      <form onSubmit={onSubmit} noValidate className="mt-6 space-y-4">
        {stateEmail ? null : (
          <AuthInput
            label="Email"
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
        <AuthInput
          label="Verification code"
          id="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          error={error}
        />
        <button
          type="submit"
          disabled={submitting || code.length !== 6 || email.length === 0}
          className="w-full rounded-md bg-emerald-700 px-4 py-2.5 font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
        >
          {submitting ? 'Verifying…' : 'Verify & sign in'}
        </button>
      </form>
      <div className="mt-4 text-sm text-stone-600">
        Didn't get it?{' '}
        <button
          type="button"
          onClick={onResend}
          disabled={cooldown > 0 || email.length === 0}
          className="font-medium text-emerald-700 hover:underline disabled:cursor-not-allowed disabled:text-stone-400 disabled:no-underline"
        >
          {cooldown > 0 ? `Resend code (${cooldown}s)` : 'Resend code'}
        </button>
      </div>
      <p className="mt-2 text-xs text-stone-500">
        Codes expire after 15 minutes. Check your spam folder if nothing arrives.
      </p>
    </section>
  );
}
